<template>
  <div class="rounded-lg bg-slate-50 p-4">
    <nav
      class="flex flex-col items-start justify-start gap-5 lg:flex-row lg:items-center lg:justify-center"
      data-id="plan-header-buttons"
      :data-subscription-schedule="selectedSchedule"
      :data-selected-currency="selectedCurrency"
    >
      <div
        class="flex flex-row items-center justify-center gap-3"
        v-if="!loading"
      >
        <span class="text-slate-800"> Billing: </span>
        <FormSelect
          :value="selectedSchedule"
          data-id="plan-schedule"
          data-test-id="plan-schedule-toggle"
          :options="scheduleOptions"
          button-variant="light"
          size="xs"
          buttons
          @change="onSchedule"
        />
      </div>
      <div class="flex flex-row items-center justify-center gap-3">
        <span class="text-slate-800"> Currency: </span>
        <FormSelect
          :value="selectedCurrency"
          :data-selected-currency="selectedCurrency"
          data-id="plan-currency"
          data-test-id="plan-currency-toggle"
          :options="currencyOptions"
          size="xs"
          buttons
          @change="onCurrency"
        />
        <span class="text-slate-800"> Trial: </span>
        <FormCheckbox
          class="d-inline"
          switch
          :value="selectedTrialEnabled"
          size="xs"
          data-id="enable-trial"
          :data-trial-enabled="selectedTrialEnabled ? '1' : '0'"
          @change="selectedTrialEnabled = !selectedTrialEnabled"
        />
      </div>
    </nav>
  </div>
</template>
<script setup lang="ts">
import FormSelect from '../../components/form/FormSelect.vue';
import type { PlanDisplaysCurrenciesEnum } from '@mailslurp/admin-sdk';
import { PlanDisplaysSchedulesEnum } from '@mailslurp/admin-sdk';
import { onMounted, ref, computed, watch } from 'vue';
import { useStore3 } from '../../store/main';
import { storeToRefs } from 'pinia';
import FormCheckbox from '../../components/form/FormCheckbox.vue';

const store3 = useStore3();

const {
  selectedCurrency,
  selectedTrialEnabled,
  selectedSchedule,
  subscription,
  allPlans,
} = storeToRefs(store3);

const props = withDefaults(
  defineProps<{
    changeIndex?: number;
    loading?: boolean;
  }>(),
  { changeIndex: 0, loading: false },
);

// currency
const currencyOptions = ref<
  {
    value: PlanDisplaysCurrenciesEnum;
    text: string;
    disabled: boolean;
  }[]
>([]);

// schedule
const scheduleOptions = ref<
  {
    value: PlanDisplaysSchedulesEnum;
    text: PlanDisplaysSchedulesEnum | string;
  }[]
>([]);

const onSchedule = (value: PlanDisplaysSchedulesEnum) => {
  store3.setSelectedSchedule(value);
};

const onCurrency = (value: PlanDisplaysCurrenciesEnum) => {
  store3.setSelectedCurrency(value);
};

const changeIndexP = computed(() => props.changeIndex);

const tForSchedule = (s: PlanDisplaysSchedulesEnum) => {
  if (s === PlanDisplaysSchedulesEnum.YEARLY) {
    return 'Yearly subscription' as string;
  } else {
    return 'Monthly subscription' as string;
  }
};

const setOptions = () => {
  const hasSubscription = subscription.value;
  currencyOptions.value = (allPlans.value?.currencies ?? []).map((currency) => {
    const text = [allPlans.value?.symbols?.[currency], currency]
      .filter((it) => it)
      .join(' ');
    const disabled = !hasSubscription
      ? false
      : currency !==
        (selectedCurrency.value as unknown as PlanDisplaysCurrenciesEnum);
    return {
      value: currency,
      text,
      disabled,
    };
  });
  scheduleOptions.value = (allPlans.value?.schedules ?? []).map((c) => ({
    value: c,
    text: tForSchedule(c),
  }));
};

onMounted(() => {
  setOptions();
});

watch(
  () => selectedCurrency.value,
  () => {
    setOptions();
  },
);

watch(
  () => selectedSchedule.value,
  () => {
    setOptions();
  },
);

watch(changeIndexP, () => {
  setOptions();
});
</script>
